import { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { Dialog } from '../../components/ui/Dialog'
import { Button } from '../../components/ui/Button'
import { FormField, Input, Select, Row } from '../../components/ui/FormField'
import { tokens } from '../../styles/tokens'
import { FoodGroup } from '../../types/catalog'
import { CatalogSuggestion, ReviewKind, UnmatchedItem } from '../../types/catalogReview'
import { catalogReviewService } from '../../services/catalogReviewService'

const Section = styled.div`
  margin-bottom: 22px;
`

const SectionTitle = styled.h3`
  font-size: 13px;
  font-weight: 600;
  color: ${tokens.purple900};
  margin: 0 0 10px;
`

const Typed = styled.p`
  margin: 0 0 18px;
  font-size: 14px;
  color: ${tokens.purple600};

  strong {
    color: ${tokens.purple900};
    font-size: 16px;
  }
`

const SuggestionList = styled.ul`
  list-style: none;
  margin: 10px 0 0;
  padding: 0;
  border: 1px solid ${tokens.purple100};
  border-radius: 10px;
  overflow: hidden;
`

const SuggestionItem = styled.li`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  border-bottom: 1px solid ${tokens.purple100};

  &:last-child {
    border-bottom: none;
  }
`

const Names = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 14px;
  color: ${tokens.purple900};

  span {
    color: ${tokens.purple400};
    font-size: 12px;
  }
`

const Score = styled.div<{ $score: number }>`
  width: 60px;
  height: 6px;
  border-radius: 3px;
  background: ${tokens.purple100};
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    inset: 0 auto 0 0;
    width: ${p => Math.round(p.$score * 100)}%;
    background: ${tokens.purple400};
  }
`

const Empty = styled.p`
  font-size: 13px;
  color: ${tokens.purple400};
  margin: 10px 0 0;
`

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid ${tokens.purple100};
  padding-top: 16px;
  font-size: 12px;
  color: ${tokens.purple400};
`

const ErrorMessage = styled.p`
  color: #a32d2d;
  font-size: 13px;
  margin: 0 0 12px;
`

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

const suggestionLabel = (s: CatalogSuggestion) => {
  const names = [s.names.en, s.names.es].filter(Boolean).join(' / ')
  const abbr = s.abbreviations
    ? ` (${[...new Set(Object.values(s.abbreviations))].join(', ')})`
    : ''
  return `${names}${abbr}`
}

interface Props {
  kind: ReviewKind
  item: UnmatchedItem | null
  token: string | null
  foodGroups: FoodGroup[]
  onClose: () => void
  /** Called after any decision with a short summary for the page */
  onDone: (summary: string) => void
}

export function ReviewDialog({ kind, item, token, foodGroups, onClose, onDone }: Props) {
  const [query, setQuery] = useState('')
  const [suggestions, setSuggestions] = useState<CatalogSuggestion[]>([])
  const [searching, setSearching] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [nameEn, setNameEn] = useState('')
  const [nameEs, setNameEs] = useState('')
  const [foodGroupId, setFoodGroupId] = useState('')

  // Reset the form for each reviewed item
  useEffect(() => {
    if (!item) return
    setQuery(item.text)
    setNameEn(capitalize(item.text))
    setNameEs(capitalize(item.text))
    setFoodGroupId('')
    setError(null)
  }, [item])

  // Debounced fuzzy search — starts with what the user typed, the admin can refine it
  useEffect(() => {
    if (!item || !query.trim()) {
      setSuggestions([])
      return undefined
    }
    setSearching(true)
    const timer = setTimeout(() => {
      catalogReviewService(token)
        .suggestions(kind, query)
        .then(res => setSuggestions(res.items))
        .catch(() => setSuggestions([]))
        .finally(() => setSearching(false))
    }, 300)
    return () => clearTimeout(timer)
  }, [item, query, kind, token])

  const run = useCallback(
    async (key: string, action: () => Promise<string>) => {
      setBusy(key)
      setError(null)
      try {
        onDone(await action())
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Something went wrong')
      } finally {
        setBusy(null)
      }
    },
    [onDone]
  )

  const text = item?.text ?? ''

  const linkedSummary = (rows: number, recipes: number, target: string) =>
    `"${text}" → ${target}: linked ${rows} ${rows === 1 ? 'row' : 'rows'} in ${recipes} ${
      recipes === 1 ? 'recipe' : 'recipes'
    }.`

  const handleMap = (s: CatalogSuggestion) =>
    run(s.id, async () => {
      const res = await catalogReviewService(token).map(kind, text, s.id)
      return linkedSummary(res.linked.rows, res.linked.recipes, suggestionLabel(s))
    })

  const handleCreate = () =>
    run('create', async () => {
      const translations = [
        { locale: 'en' as const, name: nameEn.trim() },
        { locale: 'es' as const, name: nameEs.trim() },
      ].filter(t => t.name)
      const res = await catalogReviewService(token).createIngredient({
        text,
        foodGroupId,
        translations,
      })
      return linkedSummary(
        res.linked.rows,
        res.linked.recipes,
        `new ingredient ${nameEn || nameEs}`
      )
    })

  const handleDismiss = () =>
    run('dismiss', async () => {
      await catalogReviewService(token).dismiss(kind, text)
      return `"${text}" dismissed — it stays as free text.`
    })

  const canCreate = !!foodGroupId && !!(nameEn.trim() || nameEs.trim())

  return (
    <Dialog
      open={!!item}
      title={kind === 'unit' ? 'Review unit' : 'Review ingredient'}
      onClose={onClose}
      width="640px"
    >
      {item && (
        <>
          <Typed>
            Users wrote <strong>{item.text}</strong> in {item.recipeCount}{' '}
            {item.recipeCount === 1 ? 'recipe' : 'recipes'}
            {item.variants.length > 1 && ` (also spelled ${item.variants.slice(1).join(', ')})`}.
          </Typed>

          {error && <ErrorMessage>{error}</ErrorMessage>}

          <Section>
            <SectionTitle>Is it one of these?</SectionTitle>
            <Input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search…" />
            {!searching && suggestions.length === 0 && <Empty>No matches.</Empty>}
            {suggestions.length > 0 && (
              <SuggestionList>
                {suggestions.map(s => (
                  <SuggestionItem key={s.id}>
                    <Names>{suggestionLabel(s)}</Names>
                    <Score $score={s.score} title={`Similarity ${Math.round(s.score * 100)}%`} />
                    <Button
                      size="sm"
                      variant="secondary"
                      loading={busy === s.id}
                      disabled={!!busy}
                      onClick={() => handleMap(s)}
                    >
                      This is it
                    </Button>
                  </SuggestionItem>
                ))}
              </SuggestionList>
            )}
          </Section>

          {kind === 'ingredient' && (
            <Section>
              <SectionTitle>None of these? Add it to the catalog</SectionTitle>
              <Row>
                <FormField label="English name">
                  <Input value={nameEn} onChange={e => setNameEn(e.target.value)} />
                </FormField>
                <FormField label="Spanish name">
                  <Input value={nameEs} onChange={e => setNameEs(e.target.value)} />
                </FormField>
              </Row>
              <FormField label="Food group">
                <Select value={foodGroupId} onChange={e => setFoodGroupId(e.target.value)}>
                  <option value="">Select…</option>
                  {foodGroups.map(fg => (
                    <option key={fg.id} value={fg.id}>
                      {fg.translations.find(t => t.locale === 'en')?.name ?? fg.code}
                    </option>
                  ))}
                </Select>
              </FormField>
              <Button
                onClick={handleCreate}
                loading={busy === 'create'}
                disabled={!canCreate || !!busy}
              >
                Create ingredient &amp; link
              </Button>
            </Section>
          )}

          <Footer>
            <span>Junk or not food? It stays as free text and leaves the queue.</span>
            <Button
              variant="danger"
              size="sm"
              onClick={handleDismiss}
              loading={busy === 'dismiss'}
              disabled={!!busy}
            >
              Dismiss
            </Button>
          </Footer>
        </>
      )}
    </Dialog>
  )
}
